'use client'

import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion } from 'framer-motion'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

import { evaluate } from '@/lib/calculator/engine'
import { formatCents } from '@/lib/calculator/format'

// Live estimate panel (Task 4 of docs/superpowers/plans/2026-07-14-pricing-calculator.md).
// Renders the SAME evaluate() the server uses (T2), against the client-safe
// publicConfig (T3) + the wizard's live answers — worthCents was already
// stripped server-side, so it is never in reach here even by accident.
//
// Per-item amounts are deliberately NOT rendered under the blur, even
// though the public config's addMin/addMaxCents make them computable
// client-side: the itemized dollar reveal is the capture-gated moment
// (BreakdownStack, post-submit). Blurred rows show the item's real label
// plus a fixed placeholder glyph string — never the real numbers.
const BLUR_PLACEHOLDER = '$••,•••–$••,•••'

// Fallback only — the real teaser line lives on the Calculator global
// (`teaserCopy`, seeded with this same string) per the copy-discipline
// rule; this covers a config predating the field.
const TEASER_FALLBACK =
  "Unlock your itemized breakdown — enter your details below to see exactly what's included."

// ---------------------------------------------------------------------------
// Animated number-roll: framer-motion's imperative `animate()` driving a
// motion value, mirrored into React state so the formatted text re-renders.
// Reduced motion collapses straight to the target — no interim frames.
// ---------------------------------------------------------------------------
function useAnimatedCents(target, shouldReduceMotion) {
  const motionValue = useMotionValue(target)
  const [display, setDisplay] = useState(target)

  useEffect(() => {
    const unsubscribe = motionValue.on('change', (value) => setDisplay(Math.round(value)))
    return unsubscribe
  }, [motionValue])

  useEffect(() => {
    if (shouldReduceMotion) {
      motionValue.jump(target)
      setDisplay(target)
      return
    }
    const controls = animate(motionValue, target, { duration: 0.6, ease: 'easeOut' })
    return () => controls.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, shouldReduceMotion])

  return display
}

function sectionTitleMap(publicConfig) {
  const map = new Map()
  for (const section of publicConfig?.sections ?? []) {
    if (section?.key) map.set(section.key, section.title || section.key)
  }
  return map
}

function groupItems(items, titleMap) {
  const order = []
  const bySection = new Map()
  for (const item of items) {
    if (!bySection.has(item.sectionKey)) {
      bySection.set(item.sectionKey, [])
      order.push(item.sectionKey)
    }
    bySection.get(item.sectionKey).push(item)
  }
  return order.map((key) => ({ key, title: titleMap.get(key) || key, items: bySection.get(key) }))
}

function EstimateItemRow({ item }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5" data-testid="calc-estimate-row">
      <span className="min-w-0 truncate text-sm text-white/80">{item.label}</span>
      <span
        aria-hidden="true"
        data-testid="calc-estimate-row-amount"
        className="pointer-events-none shrink-0 blur-[4px] select-none font-mono text-sm whitespace-nowrap text-white/45"
      >
        {BLUR_PLACEHOLDER}
      </span>
    </div>
  )
}

function EstimateContent({ grouped, displayMin, displayMax, shouldReduceMotion, teaserCopy }) {
  return (
    <div>
      <p className="text-xs font-semibold tracking-[0.2em] text-kingdom-gold uppercase">
        Your estimate
      </p>
      <p
        className="mt-2 font-display text-3xl font-medium text-white"
        data-testid="calc-estimate-total"
      >
        {formatCents(displayMin)} – {formatCents(displayMax)}
      </p>

      {grouped.length > 0 ? (
        <div className="mt-6 space-y-4">
          <AnimatePresence initial={false}>
            {grouped.map((section) => (
              <motion.div
                key={section.key}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.25 }}
              >
                <p className="text-[0.65rem] font-semibold tracking-widest text-white/40 uppercase">
                  {section.title}
                </p>
                <div className="mt-1.5 divide-y divide-white/5">
                  {section.items.map((item, i) => (
                    <EstimateItemRow key={`${item.questionKey}-${item.label}-${i}`} item={item} />
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <p className="pt-2 text-xs leading-relaxed text-white/40" data-testid="calc-estimate-teaser">
            {teaserCopy || TEASER_FALLBACK}
          </p>
        </div>
      ) : (
        <p className="mt-6 text-sm text-white/40">
          Answer a few questions to see your range take shape.
        </p>
      )}
    </div>
  )
}

export function EstimatePanel({ publicConfig, answers }) {
  const shouldReduceMotion = useReducedMotion()
  const [sheetOpen, setSheetOpen] = useState(false)

  // While the mobile sheet is open, lock body scroll so wheel/touch on the
  // sheet (or backdrop) can't drag the wizard underneath — same idiom as
  // the legacy pricing modal (src/components/ui/pricing-modal.jsx). Restore
  // on close AND unmount (capture completing unmounts the panel mid-open).
  useEffect(() => {
    if (sheetOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [sheetOpen])

  // Escape closes the sheet (pricing-modal precedent).
  useEffect(() => {
    if (!sheetOpen) return
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSheetOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [sheetOpen])

  const result = useMemo(() => evaluate(publicConfig, answers), [publicConfig, answers])
  const titleMap = useMemo(() => sectionTitleMap(publicConfig), [publicConfig])
  const grouped = useMemo(() => groupItems(result.items, titleMap), [result.items, titleMap])

  const displayMin = useAnimatedCents(result.totalMinCents, shouldReduceMotion)
  const displayMax = useAnimatedCents(result.totalMaxCents, shouldReduceMotion)

  return (
    <>
      {/* Desktop: sticky right column, ≥1024px. */}
      <aside className="hidden lg:sticky lg:top-28 lg:block" data-testid="calc-estimate-panel-desktop">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <EstimateContent
            grouped={grouped}
            displayMin={displayMin}
            displayMax={displayMax}
            shouldReduceMotion={shouldReduceMotion}
            teaserCopy={publicConfig?.teaserCopy}
          />
        </div>
      </aside>

      {/* Mobile: fixed bottom bar expanding to a dvh-safe sheet. */}
      <div className="lg:hidden">
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-neutral-950/95 backdrop-blur"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="calc-estimate-bar"
        >
          <button
            type="button"
            onClick={() => setSheetOpen((open) => !open)}
            aria-expanded={sheetOpen}
            data-testid="calc-estimate-bar-toggle"
            className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-3.5"
          >
            <span className="min-w-0 truncate text-sm text-white/70">
              Your estimate:{' '}
              <span className="font-semibold text-white" data-testid="calc-estimate-bar-total">
                {formatCents(displayMin)}–{formatCents(displayMax)}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-kingdom-gold">
              See breakdown
              {sheetOpen ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
            </span>
          </button>
        </div>

        <AnimatePresence>
          {sheetOpen && (
            <>
              <motion.button
                type="button"
                aria-label="Close breakdown"
                onClick={() => setSheetOpen(false)}
                className="fixed inset-0 z-40 bg-black/60"
                initial={shouldReduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
              />
              <motion.div
                className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-3xl border-t border-white/10 bg-neutral-950 px-5 pt-5"
                style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
                initial={shouldReduceMotion ? false : { y: '100%' }}
                animate={{ y: 0 }}
                exit={shouldReduceMotion ? undefined : { y: '100%' }}
                transition={
                  shouldReduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 34 }
                }
                data-testid="calc-estimate-sheet"
              >
                <div className="mx-auto h-1.5 w-10 shrink-0 rounded-full bg-white/15" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close"
                  className="absolute top-4 right-4 grid size-11 place-items-center rounded-full text-white/60 transition hover:text-white"
                >
                  <X className="size-5" />
                </button>
                <div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-2">
                  <EstimateContent
                    grouped={grouped}
                    displayMin={displayMin}
                    displayMax={displayMax}
                    shouldReduceMotion={shouldReduceMotion}
                    teaserCopy={publicConfig?.teaserCopy}
                  />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
